import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ChronologyAccordance.scss';
import { DateSegment } from './DateSegment';
import moment from 'moment';
import { capitalizeFirstLetter } from '../../../../services/stringUtil';
import { EventAccordance, getDateFormattedForEvent, getEventsInAccordance } from './EventAccordance.util';
import SpinningLoader from '../../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';
import { AccordanceState } from '../../../../models/AccordanceState';

type props = {
  accordance: any;
  log: any;
};

const ChronologyAccordance = (props: props) => {
  const { accordance, log } = props;
  const { expirationDate, newExpirationDate } = accordance;

  const [events, setEvents] = useState<any[] | null>(null);
  const [dateToExpire, setDateToExpire] = useState(new Date());
  const [dateToExpireText, setDateToExpireText] = useState('');

  const [logChangeCount, setLogChangeCount] = useState(0);
  const logChangeTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const parsedNewExpirationDate = moment(newExpirationDate.dateFormated?.replace(/"/g, ''), 'DD/MMM/YYYY').toDate();
    const parsedExpirationDate = moment(expirationDate.dateFormatedDetails?.replace(/"/g, ''), 'DD/MMM/YYYY').toDate();

    setDateToExpire(newExpirationDate.dateFormated !== '-' ? parsedNewExpirationDate : parsedExpirationDate);
    setDateToExpireText(newExpirationDate.dateFormated !== '-' ? newExpirationDate.dateFormated : expirationDate.dateFormatedDetails);
  }, [])

  useEffect(() => {
    setLogChangeCount(count => count + 1);
    if (logChangeTimeout.current) clearTimeout(logChangeTimeout.current);

    logChangeTimeout.current = setTimeout(() => {
      setEvents(getEventsInAccordance(log, accordance, t));
      setLogChangeCount(0);
    }, 800);

    return () => {
      if (logChangeTimeout.current) clearTimeout(logChangeTimeout.current);
    };
  }, [log])

  const dateColor = () => {
    if (dateToExpire.getTime() < (new Date()).getTime()) {
      return 'stylesLiRed';
    } else {
      return 'stylesLi';
    }
  };

  const getNodeTitle = (event: any) => {
    switch (event.subsection) {
      case EventAccordance.AccordancesType:
        return t('origin_of_the_agreement') + ": ";

      case EventAccordance.ApprovedByUser:
        return `${capitalizeFirstLetter(t('approved_word'))} ${t('by').toLowerCase()}`;

      case EventAccordance.PendingApprovalByUser:
        return `${capitalizeFirstLetter(t('pending_accordance_singular'))} ${t('by').toLowerCase()}`;

      case EventAccordance.Edited:
        return `${capitalizeFirstLetter(t('accordance_edited'))}:`;

      case EventAccordance.RejectedByUser:
        return `${capitalizeFirstLetter(t('rejected'))} ${t('by').toLowerCase()}`;

      case EventAccordance.Current:
        return `${capitalizeFirstLetter(t('active_accordance_singular'))}`;

      case EventAccordance.Completed:
        return `${capitalizeFirstLetter(t('accomplished_accordance_singular'))}`;

      case EventAccordance.Canceled:
        return `${capitalizeFirstLetter(t('cancelled_accordance_singular'))}`;

      default:
        return '';
    }
  }

  return (
    <div className="right-chronology">
      {
        !!events ? (
          <div className="dates">
            <section>
              <p className="section-title">{t('new_agreement_chronology').toUpperCase()}</p>
              {accordance.status !== AccordanceState.FULLFILLED && (
                <span className="inline-expiration">
                  {t('expiration')}:&nbsp;
                  <strong className={'inline-expiration-strong-' + dateColor()}>
                    {dateToExpireText}
                  </strong>
                </span>)}
            </section>
            <ul className='chronology-container'>
              {events?.map((event, index) => (
                <DateSegment
                  key={index}
                  styleCircleData={'stylesLi'}
                  nodeTitle={getNodeTitle(event)}
                  nodeSubtitle={event.subsection !== EventAccordance.Completed ? event?.data : ''}
                  data={getDateFormattedForEvent(event, accordance)}
                  currentSegment={index === 0}
                  isExpired={(dateToExpire.getTime() < (new Date()).getTime())
                    && accordance.status !== AccordanceState.FULLFILLED}
                  isRejected={event.subsection === EventAccordance.RejectedByUser}
                  isEdited={event.subsection === EventAccordance.Edited}
                />
              ))}
            </ul>
          </div>
        )
          : (<SpinningLoader customText=' ' />)
      }
    </div>
  );
};

export default ChronologyAccordance;
